import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { userAPI } from '../services/api';
import '../styles/ProfileModal.css';

export default function ProfileModal({ user, isOpen, onClose, onProfileUpdate }) {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: ''
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        full_name: user.full_name || '',
        phone: user.phone || ''
      });
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setEditMode(false);
      setMessage('');
      setError('');
    }
  }, [isOpen, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!formData.full_name.trim()) {
      setError('Full name is required');
      return;
    }

    setProfileLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await userAPI.updateProfile({
        full_name: formData.full_name,
        phone: formData.phone
      });

      setMessage('Profile updated successfully');
      setEditMode(false);

      if (onProfileUpdate) {
        onProfileUpdate(response.data);
      }

      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormData({
      full_name: user?.full_name || '',
      phone: user?.phone || ''
    });
    setError('');
  };

  const handleChangePassword = async () => {
    const oldPassword = String(passwordData.oldPassword || '');
    const newPassword = String(passwordData.newPassword || '');
    const confirmPassword = String(passwordData.confirmPassword || '');

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError('Please complete all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }

    if (newPassword.length < 6 || newPassword.length > 128) {
      setError('New password must be between 6 and 128 characters');
      return;
    }

    setPasswordLoading(true);
    setError('');
    setMessage('');

    try {
      await userAPI.changePassword({ oldPassword, newPassword, confirmPassword });
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setMessage('Password changed successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="profile-modal__header">
          <h2>Profile</h2>
          <button
            className="profile-modal__close"
            onClick={onClose}
            aria-label="Close profile"
          >
            ✕
          </button>
        </div>

        <div className="profile-modal__content">
          <div className="profile-section">
            <div className="profile-section__title">Balance</div>
            <div className="profile-balance">
              <div className="profile-balance__amount">
                {Number(user?.balance || 0).toFixed(2)}
              </div>
              <div className="profile-balance__currency">WKP</div>
            </div>
          </div>

          <div className="profile-section">
            <div className="profile-section__title">Account Information</div>

            <div className="profile-field">
              <label className="profile-field__label">Username</label>
              <div className="profile-field__display">{user?.username}</div>
            </div>

            <div className="profile-field">
              <label className="profile-field__label">Email</label>
              <div className="profile-field__display">{user?.email}</div>
            </div>

            {editMode ? (
              <>
                <div className="profile-field">
                  <label className="profile-field__label">Full Name</label>
                  <input
                    type="text"
                    name="full_name"
                    className="profile-field__input"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="profile-field">
                  <label className="profile-field__label">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    className="profile-field__input"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="profile-field">
                  <label className="profile-field__label">Full Name</label>
                  <div className="profile-field__display">
                    {formData.full_name || 'Not set'}
                  </div>
                </div>

                <div className="profile-field">
                  <label className="profile-field__label">Phone Number</label>
                  <div className="profile-field__display">
                    {formData.phone || 'Not set'}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="profile-section">
            <div className="profile-section__title">Change Password</div>

            <div className="profile-field">
              <label className="profile-field__label">Current Password</label>
              <input
                type="password"
                name="oldPassword"
                className="profile-field__input"
                value={passwordData.oldPassword}
                onChange={handlePasswordInputChange}
                placeholder="Enter current password"
                autoComplete="current-password"
              />
            </div>

            <div className="profile-field">
              <label className="profile-field__label">New Password</label>
              <input
                type="password"
                name="newPassword"
                className="profile-field__input"
                value={passwordData.newPassword}
                onChange={handlePasswordInputChange}
                placeholder="Enter new password"
                autoComplete="new-password"
              />
            </div>

            <div className="profile-field">
              <label className="profile-field__label">Confirm New Password</label>
              <input
                type="password"
                name="confirmPassword"
                className="profile-field__input"
                value={passwordData.confirmPassword}
                onChange={handlePasswordInputChange}
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
            </div>

            <div className="profile-field">
              <div className="profile-field__display">
                Password must be 6–128 characters.
              </div>
            </div>

            <button
              className="profile-modal__btn profile-modal__btn--secondary"
              type="button"
              onClick={handleChangePassword}
              disabled={passwordLoading}
            >
              {passwordLoading ? 'Updating Password...' : 'Update Password'}
            </button>
          </div>

          {message && (
            <div className="profile-modal__message profile-modal__message--success">
              {message}
            </div>
          )}

          {error && (
            <div className="profile-modal__message profile-modal__message--error">
              {error}
            </div>
          )}
        </div>

        <div className="profile-modal__footer">
          {editMode ? (
            <>
              <button
                className="profile-modal__btn profile-modal__btn--secondary"
                onClick={handleCancel}
                disabled={profileLoading || passwordLoading}
              >
                Cancel
              </button>
              <button
                className="profile-modal__btn profile-modal__btn--primary"
                onClick={handleSave}
                disabled={profileLoading || passwordLoading}
              >
                {profileLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              className="profile-modal__btn profile-modal__btn--primary"
              onClick={() => setEditMode(true)}
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
